import React, { useState } from 'react';
import {
  Box,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Stack,
  Tag as ChakraTag,
  TagLabel,
  TagCloseButton,
  Text,
  Wrap,
  WrapItem,
  useColorModeValue,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  useDisclosure,
  IconButton,
  HStack,
  useToast,
  Spinner,
} from '@chakra-ui/react';
import { FiTag, FiPlus, FiZap } from 'react-icons/fi';
import { useTags } from '../../../contexts/TagsContext';
import { useEncryption } from '../../../contexts/EncryptionContext';
import axios from 'axios';
import env from 'config/env';

interface TagSelectorProps {
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  content?: string;
}

const TagSelector: React.FC<TagSelectorProps> = ({
  selectedTags,
  onTagsChange,
  content,
}) => {
  const { tags, createTag, refreshTags } = useTags();
  const { encrypt } = useEncryption();
  const [isOpen, setIsOpen] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#000000');
  const [isGenerating, setIsGenerating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const toast = useToast();

  const handleTagSelect = (tagId: string) => {
    if (selectedTags.includes(tagId)) {
      onTagsChange(selectedTags.filter((id) => id !== tagId));
    } else {
      onTagsChange([...selectedTags, tagId]);
    }
  };

  const handleCreateTag = async () => {
    try {
      const result = await createTag(newTagName, newTagColor);
      const newTagId = typeof result === 'string' ? result : result.id;
      onTagsChange([...selectedTags, newTagId]);
      setNewTagName('');
      setNewTagColor('#000000');
      await refreshTags();
      onModalClose();
      setIsOpen(false);
    } catch (error) {
      console.error('Error creating tag:', error);
      toast({
        title: 'Error',
        description: 'Failed to create tag.',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleGenerateTags = async () => {
    if (!content) {
      toast({
        title: 'No content',
        description: 'Please write some content first to generate tags.',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsGenerating(true);
    try {
      // Get existing tag names
      const existingTags = tags.map(tag => tag.decrypted_name || '').filter(Boolean);

      const response = await axios.post(`${env.API_URL}/ai/generate-tags`, {
        content,
        existingTags
      });

      const suggestedTags = response.data.tags;

      // Create new tags and add them to selection
      const newTagIds = await Promise.all(
        suggestedTags.map(async (tag: { name: string; id: string | null; isNew: boolean }) => {
          if (tag.isNew) {
            // Create new tag with a random color
            const color = '#' + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0');
            const result = await createTag(tag.name, color);
            return typeof result === 'string' ? result : result.id;
          }
          return tag.id;
        })
      );

      // Add new tags to selection (filter out null values)
      const validNewTags = newTagIds.filter((id): id is string => id !== null);
      const updatedTags = Array.from(new Set([...selectedTags, ...validNewTags]));
      onTagsChange(updatedTags);

      // Refresh the tags list to show new tags in the search
      await refreshTags();
      setIsOpen(false);
      
      toast({
        title: 'Tags generated',
        description: 'New tags have been generated and added.',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error generating tags:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate tags.',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Filter tags based on search query
  const filteredTags = tags.filter((tag) => 
    !selectedTags.includes(tag.id) && 
    (tag.decrypted_name?.toLowerCase() || '').includes(searchQuery.toLowerCase())
  );

  return (
    <Box>
      <HStack spacing={2} mb={2}>
        <Text fontWeight="medium">Tags</Text>
        <IconButton
          aria-label="Auto-generate tags"
          icon={isGenerating ? <Spinner size="sm" /> : <FiZap />}
          size="sm"
          onClick={handleGenerateTags}
          isDisabled={!content || isGenerating}
          title="Auto-generate tags"
        />
      </HStack>
      <Wrap spacing={2} mb={2}>
        {selectedTags.map((tagId) => {
          const tag = tags.find((t) => t.id === tagId);
          if (!tag) return null;
          return (
            <WrapItem key={tagId}>
              <ChakraTag size="md">
                <Box
                  w="2"
                  h="2"
                  borderRadius="full"
                  bg={tag.color}
                  mr="2"
                />
                <TagLabel>{tag.decrypted_name}</TagLabel>
                <TagCloseButton
                  onClick={() => handleTagSelect(tagId)}
                />
              </ChakraTag>
            </WrapItem>
          );
        })}
      </Wrap>
      <HStack>
        <Popover
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
        >
          <PopoverTrigger>
            <Button
              leftIcon={<FiTag />}
              size="sm"
              variant="outline"
              onClick={() => setIsOpen(true)}
            >
              Add Tags
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <PopoverBody>
              <Stack spacing={2}>
                <Input
                  placeholder="Search tags..."
                  size="sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  mb={2}
                />
                <Box maxHeight="200px" overflowY="auto">
                  <Stack spacing={2}>
                    {filteredTags.map((tag) => (
                      <ChakraTag
                        key={tag.id}
                        size="md"
                        cursor="pointer"
                        onClick={() => {
                          handleTagSelect(tag.id);
                          setIsOpen(false);
                        }}
                      >
                        <Box
                          w="2"
                          h="2"
                          borderRadius="full"
                          bg={tag.color}
                          mr="2"
                        />
                        <TagLabel>{tag.decrypted_name}</TagLabel>
                      </ChakraTag>
                    ))}
                  </Stack>
                </Box>
                <Divider />
                <Button
                  leftIcon={<FiPlus />}
                  size="sm"
                  onClick={() => {
                    onModalOpen();
                    setIsOpen(false);
                  }}
                >
                  Create New Tag
                </Button>
              </Stack>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </HStack>

      <Modal isOpen={isModalOpen} onClose={onModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New Tag</ModalHeader>
          <ModalBody>
            <FormControl>
              <FormLabel>Name</FormLabel>
              <Input
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="Enter tag name"
              />
            </FormControl>
            <FormControl mt={4}>
              <FormLabel>Color</FormLabel>
              <Input
                type="color"
                value={newTagColor}
                onChange={(e) => setNewTagColor(e.target.value)}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onModalClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCreateTag}
              isDisabled={!newTagName}
            >
              Create
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TagSelector; 